import * as moment from 'moment';

import { AddressModel } from './address';
import {
  BaseEntity,
  Default,
  MemoizeGetters,
  Transform,
  toDate,
  toEntity,
} from './base';
import { DeliveryLocation } from './delivery-location';
import { EtaModel } from './eta';
import { OpsUserModel } from './user.ops';
import { OpsVehicleModel } from './vehicle.ops';
import { ServiceModel } from './service';
import { ShiftETA, ShiftModel, ShiftName, ShiftTime } from './shift';

export enum OrderStatus {
  Pending = 'pending',
  Claimed = 'claimed',
  Pumping = 'pumping',
  Complete = 'complete',
  Cancelled = 'cancelled',
  Proposed = 'proposed',
  Rejected = 'rejected',
  Deleted = 'deleted',
  MissingPayment = 'missing_valid_card',
}

export enum OnTimeStatus {
  Early = 'early',
  Available = 'available',
  Late = 'late',
  Priority = 'priority',
  AtRisk = 'at-risk',
}

export enum OrderCancelReason {
  OwnerMissing = 'owner_missing',
  Placeholder = 'placeholder',
}

export interface OrderItem {
  serviceName: string;
  description: string;
  quantity: number;
}

@MemoizeGetters
export class OpsOrderModel extends BaseEntity {
  readonly AT_RISK_HOUR_THRESHOLD = 1;
  readonly PRIORITY_HOUR_THRESHOLD = 4;

  amountCharged: number;
  needsPin: boolean;
  numGallonsPumped: number;

  shiftEndTime?: ShiftTime;
  shiftStartTime?: ShiftTime;
  shiftWindow?: ShiftName;
  status?: OrderStatus;
  virtual?: boolean;

  @Transform(toDate)
  slotStartTime?: Date;

  @Transform(toDate)
  slotEndTime?: Date;

  timeScheduled?: ShiftName;
  eta?: ShiftETA; // Todo: User EtaModel
  inspectionFormUrl: string;
  inspectionFormAppUrl?: string;

  @Transform(toDate)
  date: Date;

  @Transform(toDate)
  pumpingEta?: Date;

  @Transform(toDate)
  timeCompleted?: Date;

  @Transform(toEntity(EtaModel))
  firstEta?: EtaModel;

  @Transform(toEntity(AddressModel))
  address?: AddressModel;

  @Transform(toEntity(OpsVehicleModel))
  vehicle: OpsVehicleModel;

  @Transform(toEntity(OpsUserModel))
  user?: OpsUserModel;

  @Transform(toEntity(DeliveryLocation))
  deliveryLocation: DeliveryLocation;

  @Transform(toEntity(ServiceModel))
  service: ServiceModel;

  @Default([])
  orderItems: OrderItem[];

  lat: string;
  lng: string;

  meetingUrl?: string;
  gasRefill?: boolean;

  constructor(json: any) {
    super(json);
    this.shiftStartTime =
      json.shift_start_time || ShiftModel.defaultStartTime(this.shiftWindow);
    this.shiftEndTime =
      json.shift_end_time || ShiftModel.defaultEndTime(this.shiftWindow);
  }

  get completedOnTime() {
    if (!this.timeCompleted) {
      return false;
    }
    return (
      this.timeWindowStart.isBefore(this.timeCompleted) &&
      this.timeWindowEnd.isAfter(this.timeCompleted)
    );
  }

  currentOnTimeStatus(): OnTimeStatus {
    const now = moment();
    const startTime = this.timeWindowStart;
    const endTime = this.timeWindowEnd;

    if (!startTime || !endTime) {
      return OnTimeStatus.Late;
    }

    if (now.isBefore(startTime)) {
      return OnTimeStatus.Early;
    }

    if (now.isAfter(endTime)) {
      return OnTimeStatus.Late;
    }

    if (endTime.diff(now, 'seconds') <= this.AT_RISK_HOUR_THRESHOLD * 3600) {
      return OnTimeStatus.AtRisk;
    }

    if (endTime.diff(now, 'seconds') <= this.PRIORITY_HOUR_THRESHOLD * 3600) {
      return OnTimeStatus.Priority;
    }

    if (now.isBetween(startTime, endTime)) {
      return OnTimeStatus.Available;
    }

    return OnTimeStatus.Late;
  }

  get timeWindowString() {
    if (this.slotStartTime && this.slotEndTime) {
      const options: Intl.DateTimeFormatOptions = {
        hour: 'numeric',
        minute: '2-digit',
      };
      const startTime = this.slotStartTime.toLocaleTimeString('en-us', options);
      const endTime = this.slotEndTime.toLocaleTimeString('en-us', options);
      const timeZone = new Date()
        .toLocaleTimeString('en-us', { timeZoneName: 'short' })
        .split(' ')[2];
      return `${startTime}-${endTime} (${timeZone})`;
    }
    return `${this.timeWindowStart.format('h:mmA')}-${this.timeWindowEnd.format(
      'h:mmA'
    )}`;
  }

  private get timeWindowStart(): moment.Moment {
    const shiftStartTime = moment(this.date);
    shiftStartTime.set('hour', this.shiftStartTime.hour);
    shiftStartTime.set('minutes', this.shiftStartTime.minute);
    return shiftStartTime;
  }

  private get timeWindowEnd(): moment.Moment {
    let end: moment.Moment;
    const shiftEndTime = new Date();
    shiftEndTime.setHours(this.shiftEndTime.hour);
    shiftEndTime.setMinutes(this.shiftEndTime.minute);
    end = moment(shiftEndTime);
    return end.isBefore(this.timeWindowStart) ? end.add(1, 'day') : end;
  }

  dayOfTheWeek(relative = true): string {
    let allDays = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ];

    if (relative) {
      if (this.isToday) {
        return 'today';
      }
      if (this.isTomorrow) {
        return 'tomorrow';
      }
    }

    return allDays[this.date.getDay()];
  }

  get relativeDate() {
    if (this.isToday) {
      return 'today';
    }
    if (this.isTomorrow) {
      return 'tomorrow';
    }
    let momentDate = moment(this.date);
    return momentDate.format('dddd MMMM Do');
  }

  /**
   * Useful to check if drivers are trying to claim/complete an order at the wrong time.
   * @returns {boolean} true if the current time is too far outside of the eta window
   */
  outsideTargetTimeWindow(date: Date) {
    const ETA_HOUR_WARNING_LIMIT = 0.5;
    return (
      this.timeWindowStart.hour() - moment(date).hour() >=
      ETA_HOUR_WARNING_LIMIT
    );
  }

  get etaString(): string {
    if (this.pumpingEta) {
      let momentDate = moment(this.pumpingEta);
      return momentDate.format('h:mmA');
    }
  }

  get isToday(): boolean {
    return new Date().toDateString() == this.date.toDateString();
  }

  get isTomorrow(): boolean {
    let tomorrow = new Date();
    tomorrow.setDate(new Date().getDate() + 1);
    return tomorrow.toDateString() == this.date.toDateString();
  }

  get hasDeliveryCoordinates(): boolean {
    return (
      this.deliveryLocation &&
      this.deliveryLocation.lat != null &&
      this.deliveryLocation.lng != null
    );
  }
}

export interface OrderParams {
  vehicle_uid?: string;
  user_address_uid?: string;
  date?: string;
  shift_window?: string;
  shift_start_time?: number;
  shift_end_time?: number;
  frequency?: number;
  quote?: boolean;
  delivery_location_lat?: number;
  delivery_location_lng?: number;
  delivery_location_details?: string;
}

export interface OrderCompleteParams {
  tank_uid?: string;
  num_gallons_pumped?: number;
  override_wrong_fuel_type?: boolean;
  note?: string;
}

export interface OrderConfirmParams {
  delivery_location_lat?: number;
  delivery_location_lng?: number;
  delivery_location_details?: string;
  user_address_uid?: string;
}

export interface OrderFilterParams {
  date_from?: string;
  status?: string;
}

export interface CreateAndCompleteOrderParams {
  account_uid: string;
  user_address_uid: string;
  service_uid: string;
  vin?: string;
  license?: string;
  serial_number?: string;
  vehicle_model_uid?: string;
  vehicle_uid?: string;
  tank_uid?: string;
  num_gallons_pumped?: number;
  note?: string;
}

export interface CancelledOrderParams {
  account_uid: string;
  user_address_uid: string;
  service_uid: string;
  cancellation_reason: string;
  vin?: string;
  license?: string;
  serial_number?: string;
  vehicle_model_uid?: string;
  vehicle_uid?: string;
  tank_uid?: string;
}



// WEBPACK FOOTER //
// ./src/models/order.ops.ts